/**
 * Life4Earth V2 — React hook for content sections (entity-polymorphic).
 *
 * Migration 167 (2026-05-19) unified the About backend so Mini-Project
 * and Full Project share the same `project_content_sections` table
 * via entity_type / entity_guid. This hook accepts (entityType, entityGuid)
 * so the same code path serves both tiers.
 *
 * Backwards-compat overload: passing just a projectGuid (string) treats
 * it as entityType='project' for any consumer that hasn't migrated yet.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchSections,
  createSection,
  updateSection,
  deleteSection,
  reorderSections,
} from "./projectContentSections";
import type {
  ContentSectionEntityType,
  ProjectContentSectionResponse,
  CreateContentSectionRequest,
  UpdateContentSectionRequest,
  ReorderItem,
} from "./projectContentSections";
import {
  fetchForKey,
  invalidateAndRefetch,
  CACHE_UPDATE_EVENT,
  QUERY_KEYS,
} from "./queryCache";

// ── Hook result type ────────────────────────────────────────────

export interface UseApiProjectSectionsResult {
  sections: ProjectContentSectionResponse[];
  loading: boolean;
  error: string | null;
  source: "api" | "seed" | "loading";
  refetch: () => void;
  addSection: (body: CreateContentSectionRequest) => Promise<ProjectContentSectionResponse>;
  editSection: (sectionGuid: string, body: UpdateContentSectionRequest) => Promise<void>;
  removeSection: (sectionGuid: string) => Promise<void>;
  reorder: (items: ReorderItem[]) => Promise<void>;
}

// ── Hook ────────────────────────────────────────────────────────

/**
 * Polymorphic overload. Pass either:
 *   useApiProjectSections(projectGuid)
 *   useApiProjectSections('mini_project', miniProjectGuid)
 *   useApiProjectSections('project', projectGuid)
 */
export function useApiProjectSections(
  projectGuid: string | undefined,
): UseApiProjectSectionsResult;
export function useApiProjectSections(
  entityType: ContentSectionEntityType,
  entityGuid: string | undefined,
): UseApiProjectSectionsResult;
export function useApiProjectSections(
  arg1: string | ContentSectionEntityType | undefined,
  arg2?: string | undefined,
): UseApiProjectSectionsResult {
  // Normalize overload arguments. arg1 is the entity TYPE when it matches a known
  // type token; otherwise it's the bare projectGuid (single-arg overload).
  const isTypeArg = arg1 === "project" || arg1 === "mini_project" || arg1 === "chronicle";
  const entityType: ContentSectionEntityType = isTypeArg ? (arg1 as ContentSectionEntityType) : "project";
  const entityGuid: string | undefined = isTypeArg ? arg2 : (arg1 as string | undefined);

  const mountedRef = useRef(true);
  const cacheKey = entityGuid
    ? `${QUERY_KEYS.projectSections}:${entityType}:${entityGuid}`
    : "";

  const [state, setState] = useState<
    Pick<UseApiProjectSectionsResult, "sections" | "loading" | "error" | "source">
  >({
    sections: [],
    loading: true,
    error: null,
    source: "loading",
  });

  const load = useCallback(
    async (force = false) => {
      if (!entityGuid || !mountedRef.current) return;
      try {
        const result = await fetchForKey(
          cacheKey,
          () => fetchSections(entityType, entityGuid),
          { force },
        );
        if (mountedRef.current) {
          setState({ sections: result, loading: false, error: null, source: "api" });
        }
      } catch (err: any) {
        if (mountedRef.current) {
          setState((prev) => ({ ...prev, loading: false, error: err.message, source: "seed" }));
        }
      }
    },
    [entityType, entityGuid, cacheKey],
  );

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => { mountedRef.current = false; };
  }, [load]);

  useEffect(() => {
    function onCacheUpdate(e: Event) {
      const key = (e as CustomEvent).detail?.key;
      if (key === QUERY_KEYS.projectSections || key === cacheKey) {
        load(true);
      }
    }
    window.addEventListener(CACHE_UPDATE_EVENT, onCacheUpdate);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, onCacheUpdate);
  }, [cacheKey, load]);

  const addSection = useCallback(
    async (body: CreateContentSectionRequest) => {
      if (!entityGuid) throw new Error("No entity GUID");
      const created = await createSection(entityType, entityGuid, body);
      invalidateAndRefetch(cacheKey);
      return created;
    },
    [entityType, entityGuid, cacheKey],
  );

  const editSection = useCallback(
    async (sectionGuid: string, body: UpdateContentSectionRequest) => {
      if (!entityGuid) throw new Error("No entity GUID");
      await updateSection(entityType, entityGuid, sectionGuid, body);
      invalidateAndRefetch(cacheKey);
    },
    [entityType, entityGuid, cacheKey],
  );

  const removeSection = useCallback(
    async (sectionGuid: string) => {
      if (!entityGuid) throw new Error("No entity GUID");
      await deleteSection(entityType, entityGuid, sectionGuid);
      invalidateAndRefetch(cacheKey);
    },
    [entityType, entityGuid, cacheKey],
  );

  const reorder = useCallback(
    async (items: ReorderItem[]) => {
      if (!entityGuid) throw new Error("No entity GUID");
      await reorderSections(entityType, entityGuid, items);
      invalidateAndRefetch(cacheKey);
    },
    [entityType, entityGuid, cacheKey],
  );

  return {
    ...state,
    refetch: () => load(true),
    addSection,
    editSection,
    removeSection,
    reorder,
  };
}
